import { useEffect, useRef, useState } from 'react';

/**
 * Daemon-offline strip per docs/DESIGN-DECISIONS.md §1.5 and brand/BRAND.md
 * "Daemon-offline composer strip".
 *
 * Slides above the composer when the daemon is offline. Voice is plain:
 * "daemon offline · queued locally". The strip states what is happening; it
 * does not apologize and does not warn.
 *
 * Swipe horizontally to dismiss. Returns automatically the next time the
 * parent's `seedKey` changes (i.e., on next message send), or whenever the
 * `online` flag flips from true back to false.
 */

interface Props {
  online: boolean;
  queued: number;
  /** Increment to force the strip to reappear (e.g., on next message send). */
  seedKey: number;
}

const DISMISS_DISTANCE = 80;

export function DaemonOfflineStrip({ online, queued, seedKey }: Props) {
  const [dismissed, setDismissed] = useState(false);
  const [dragX, setDragX] = useState(0);
  const startXRef = useRef<number | null>(null);

  // When daemon flips back to offline OR seedKey changes (a new send), un-dismiss.
  useEffect(() => {
    setDismissed(false);
    setDragX(0);
    startXRef.current = null;
  }, [online, seedKey]);

  // Per docs/DESIGN-UX-ARCH.md §2.3: the strip "earns its space only when the
  // state is actionable." DaemonStatus icon already conveys offline at rest;
  // the strip surfaces only when something is actually queued.
  if (online || dismissed || queued <= 0) return null;

  function onPointerDown(e: React.PointerEvent<HTMLDivElement>) {
    startXRef.current = e.clientX;
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  }

  function onPointerMove(e: React.PointerEvent<HTMLDivElement>) {
    if (startXRef.current === null) return;
    const dx = e.clientX - startXRef.current;
    setDragX(dx);
  }

  function onPointerUp(e: React.PointerEvent<HTMLDivElement>) {
    const dx = startXRef.current === null ? 0 : e.clientX - startXRef.current;
    startXRef.current = null;
    if (Math.abs(dx) >= DISMISS_DISTANCE) {
      setDismissed(true);
      setDragX(0);
    } else {
      setDragX(0);
    }
  }

  return (
    <div
      role="status"
      aria-live="polite"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={() => {
        startXRef.current = null;
        setDragX(0);
      }}
      style={{ transform: `translateX(${dragX}px)`, opacity: 1 - Math.min(1, Math.abs(dragX) / 200) }}
      className="h-8 w-full bg-night-900 flex items-center pl-4 pr-2 select-none touch-pan-y
                 border-l-2 transition-[opacity] flex-shrink-0"
    >
      <span
        style={{ borderLeftColor: 'var(--agent-edge-soft)' }}
        aria-hidden="true"
        className="absolute left-0 top-0 bottom-0 w-0.5"
      />
      <span
        className="font-mono text-[10px] uppercase tracking-mono-wide flex-1"
        style={{ color: 'var(--agent-fg-soft)' }}
      >
        daemon offline · {queued} queued locally
      </span>
    </div>
  );
}
